import { MUISelect } from "@abb/arcadia-ui-react"
import { zodResolver } from "@hookform/resolvers/zod"
import { StatusCodes } from "http-status-codes"
import { useCallback, useContext, useEffect, useState } from "react"
import { Controller, useForm } from "react-hook-form"
import { Navigate, useNavigate, useParams } from "react-router-dom"
import { toast } from "react-toastify"
import { z } from "zod"
import { useApplicationPermissions } from "../../../../../hooks/useApplicationPermissions"
import { useTrainingPermissions } from "../../../../../hooks/useTrainingPermissions"
import { digitalManagerApi } from "../../../../../services/api"
import { FieldRow, FormPage } from "../../../../components/FormPage"
import { UserContext } from "../../../../context/UserContext"
import { ApplicationPermissionGet } from "../../../../models/ApplicationPermission"
import {
  AccessLevel,
  DimaFeatureShortNameEnum,
  hasUserAccessToApplicationFeature,
} from "../../../../models/Authorization"
import { TrainingGet } from "../../../../models/Training"
import {
  TrainingPermissionGet,
  TrainingPermissionPost,
} from "../../../../models/TrainingPermission"
import { ControllerRendererProps } from "../../../../types/reactHookForm"

const validationSchema = z.object({
  permissionId: z.string().min(1, "Permission is required"),
})

export function TrainingPermissionCreate() {
  const { trainingId } = useParams()
  const { user } = useContext(UserContext)
  const [training, setTraining] = useState<TrainingGet>()
  const { trainingPermissions } = useTrainingPermissions(trainingId)
  const navigate = useNavigate()
  const returnURL = `/trainings/${trainingId}#permissions`

  useEffect(() => {
    if (trainingId) {
      digitalManagerApi
        .get<TrainingGet>(`/trainings/${trainingId}`)
        .then((res) => {
          setTraining(res.data)
        })
        .catch(() => {
          navigate(returnURL)
        })
    }
  }, [navigate, trainingId, returnURL])

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { errors, isSubmitting, isDirty, isValid },
  } = useForm<{ permissionId: string }>({
    resolver: zodResolver(validationSchema),
    mode: "onChange",
  })

  useEffect(() => {
    reset({
      permissionId: "",
    })
  }, [reset])

  const onSubmit = async (values: { permissionId: string }) => {
    if (!values.permissionId) {
      return
    }

    const body: TrainingPermissionPost = { permissionId: values.permissionId }

    await digitalManagerApi
      .post(`/trainings/${trainingId}/permissions`, body)
      .then((res) => {
        if (res.status === StatusCodes.CREATED) {
          toast.success("Permission added to training")
          navigate(returnURL)
        }
      })
      .catch(() => {
        toast.error("Cannot add permission to training")
      })
  }

  const { applicationPermissions, isLoadingApplicationPermissions } =
    useApplicationPermissions(training?.application.id)

  const handleGetOptionDisabled = useCallback(
    (option: ApplicationPermissionGet) =>
      trainingPermissions.some(
        (trainingPermission: TrainingPermissionGet) =>
          trainingPermission.permission.id === option.id
      ),
    [trainingPermissions]
  )

  const handleChangeValues = useCallback(
    (next: Array<string | { id: string }>) => {
      const firstItem = next?.[0]
      const selectedId = typeof firstItem === "string" ? firstItem : firstItem?.id
      setValue("permissionId", selectedId ?? "", {
        shouldValidate: true,
        shouldDirty: true,
      })
    },
    [setValue]
  )

  const renderPermissionSelect = useCallback(
    ({ field }: ControllerRendererProps<{ permissionId: string }, "permissionId">) => (
      <MUISelect
        id="permissionId"
        name="permissionId"
        placeholder="Select permission"
        size={32}
        dropdownLOV={applicationPermissions.map((opt) => ({
          id: opt.id,
          name: opt.name ?? "",
          isDisabled: handleGetOptionDisabled(opt),
        }))}
        enableSearch
        clearable={false}
        isDisabled={!training || isLoadingApplicationPermissions}
        selectedItem={field.value ? [field.value] : []}
        onChange={handleChangeValues}
        errors={
          typeof errors.permissionId?.message === "string"
            ? { permissionId: { message: errors.permissionId.message } }
            : undefined
        }
      />
    ),
    [
      applicationPermissions,
      handleGetOptionDisabled,
      handleChangeValues,
      training,
      isLoadingApplicationPermissions,
      errors.permissionId?.message,
    ]
  )

  if (
    training &&
    !hasUserAccessToApplicationFeature(
      user?.authorizations.applications ?? [],
      training.application.id,
      DimaFeatureShortNameEnum.TM,
      AccessLevel.Write
    )
  ) {
    return <Navigate to={returnURL} />
  }

  return (
    <FormPage
      returnURL={returnURL}
      onSubmit={handleSubmit(onSubmit)}
      isSubmitting={isSubmitting}
      isDirty={isDirty}
      isValid={isValid}
    >
      <FieldRow label="Permission">
        <Controller
          name="permissionId"
          control={control}
          render={renderPermissionSelect}
        />
      </FieldRow>
    </FormPage>
  )
}
